import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import CargoNumber from '../cargoNumber';
import { ChangeLogEntryWrapper, ChangeLogLabel } from './ChangeLog.styles';
import { ComparisonVoyage } from 'src/utils/stowage/stowagePlanComparison/comparisonMapper';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { AdjustedCargoTemperature } from 'src/types/ViewModel/Stowage/AdjustedCargoTemperature';
import { BlockedTank } from 'src/types/ViewModel/Stowage/BlockedTank';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';

type ChangeLogRotationProps = {
    groupedImosSyncData: ComparisonVoyage[];
    stowageItems: Stowage[];
    adjustedCargoTemperatures: AdjustedCargoTemperature[];
    blockedTanks: BlockedTank[];
};

const ChangeLogRotationComponent: React.FC<ChangeLogRotationProps> = ({
    groupedImosSyncData,
    stowageItems,
    adjustedCargoTemperatures,
    blockedTanks,
}: ChangeLogRotationProps) => {
    const sortComparisonVoyages = (comparisonVoyages: ComparisonVoyage[]): ComparisonVoyage[] => {
        if (!comparisonVoyages?.length) {
            return [];
        }

        comparisonVoyages.sort((a, b) => Number(a.voyageNumber) - Number(b.voyageNumber));
        for (const voyage of comparisonVoyages) {
            voyage.portCalls.sort((a, b) => a.portOrderNumber - b.portOrderNumber);
            for (const portCall of voyage.portCalls) {
                portCall.berthCalls.sort((a, b) => a.berthSequenceNumber - b.berthSequenceNumber);
                for (const berthCall of portCall.berthCalls) {
                    berthCall.cargoOperations.sort((a, b) => a.cargoOperationSequenceNumber - b.cargoOperationSequenceNumber);
                }
            }
        }
        return comparisonVoyages;
    };

    const Description = (item: any, key: string) => {
        const descriptions = item.extraData?.descriptions;
        if (!descriptions || descriptions.length === 0) return <></>;

        return descriptions.map((description, index) => (
            <div key={`description-${key}-${index}`} className="change-description">
                {description}
            </div>
        ));
    };

    const ChangeLogEntry = ({ descriptor, descriptionItems }: { descriptor: string | JSX.Element; descriptionItems: any[] }) => {
        return (
            <ChangeLogEntryWrapper>
                <ChangeLogLabel>{descriptor}</ChangeLogLabel>
                <div>
                    {descriptionItems.map((descriptionItem, index) => (
                        <span key={`descriptor_${index}`}>
                            {Description(descriptionItem, typeof descriptor === 'string' ? descriptor : 'element')}
                        </span>
                    ))}
                </div>
            </ChangeLogEntryWrapper>
        );
    };

    const getCargoOperationDescriptionItems = (cargoOperation): any[] => {
        let descriptionItems = [cargoOperation];
        if (cargoOperation.cargo.extraData?.descriptions?.length > 0) {
            descriptionItems.push(cargoOperation.cargo);
        }

        if (stowageItems?.length > 0 && stowageItems.some((stowageItem) => stowageItem.cargoOperationGuid === cargoOperation.cargoOperationGuid)) {
            descriptionItems = descriptionItems.concat(getStowageItemDescriptionItems(cargoOperation));
        }
        if (adjustedCargoTemperatures?.length > 0 && adjustedCargoTemperatures.some((a) => a.cargoGuid === cargoOperation.cargoGuid)) {
            descriptionItems = descriptionItems.concat(getAdjustedCargoTemperatureDescriptionItems(cargoOperation));
        }
        return descriptionItems;
    };

    const getStowageItemDescriptionItems = (cargoOperation: RotationCargoOperation) =>
        stowageItems.filter((stowageItem) => stowageItem.cargoOperationGuid === cargoOperation.cargoOperationGuid);
    const getAdjustedCargoTemperatureDescriptionItems = (cargoOperation: RotationCargoOperation) =>
        adjustedCargoTemperatures.filter((a) => a.cargoGuid === cargoOperation.cargoGuid);
    const getBlockedTankBerthDescriptionItems = (berthCall: RotationBerthCall) =>
        blockedTanks.filter((b) => b.fromBerthCallGuid === berthCall.berthCallGuid);
    const getBlockedTankPortDescriptionItems = (portCall: RotationPortCall) =>
        blockedTanks.filter((b) => !b.fromBerthCallGuid && b.fromPortCallGuid === portCall.portCallGuid);
    const hasBlockedTanksInBerthCall = (berthCallGuid: string) => blockedTanks.some((tank) => tank.fromBerthCallGuid === berthCallGuid);
    const hasBlockedTanksInPortCall = (portCall: RotationPortCall) => blockedTanks.some((tank) => tank.fromPortCallGuid === portCall.portCallGuid);

    return (
        <li className="dd-item">
            {groupedImosSyncData && (
                <ul>
                    {sortComparisonVoyages(groupedImosSyncData).map((voyage) => (
                        <li key={'voy_' + voyage.voyageNumber}>
                            <ChangeLogLabel>{voyage.voyageNumber}</ChangeLogLabel>
                            <ul className="tree-port-calls">
                                {voyage.portCalls.map((portCall, index) => (
                                    <li key={'portCall_' + portCall.portCallGuid + '_' + index}>
                                        <ChangeLogEntry
                                            descriptor={portCall.portDisplayName}
                                            descriptionItems={([portCall] as any[]).concat(
                                                hasBlockedTanksInPortCall(portCall) ? getBlockedTankPortDescriptionItems(portCall) : []
                                            )}
                                        />
                                        <ul className="tree-berth-calls">
                                            {portCall.berthCalls.map((berthCall, index) => (
                                                <li key={'berthCall_' + berthCall.berthCallGuid + '_' + index}>
                                                    <ChangeLogEntry
                                                        descriptor={berthCall.berthName}
                                                        descriptionItems={([berthCall] as any[]).concat(
                                                            hasBlockedTanksInBerthCall(berthCall.berthCallGuid)
                                                                ? getBlockedTankBerthDescriptionItems(berthCall)
                                                                : []
                                                        )}
                                                    />
                                                    <ul className="tree-cargo-operations">
                                                        {berthCall.cargoOperations.map((cargoOperation, index) => (
                                                            <li key={'cargoOperation_' + cargoOperation.cargoOperationGuid + '_' + index}>
                                                                {cargoOperation.cargo && (
                                                                    <div>
                                                                        <ChangeLogEntry
                                                                            descriptor={
                                                                                <div>
                                                                                    <CargoNumber
                                                                                        includeColoredBackground
                                                                                        cargo={cargoOperation.cargo}
                                                                                    />
                                                                                </div>
                                                                            }
                                                                            descriptionItems={getCargoOperationDescriptionItems(cargoOperation)}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                ))}
                                {voyage.rogueCargoes && (
                                    <>
                                        <br />
                                        <br />
                                        {voyage.rogueCargoes.map((rogueCargo, index) => (
                                            <li key={'rogueCargo' + rogueCargo.cargoGuid + '_' + index}>
                                                <ChangeLogEntry
                                                    descriptor={
                                                        <div>
                                                            <CargoNumber includeColoredBackground cargo={rogueCargo} />
                                                        </div>
                                                    }
                                                    descriptionItems={[rogueCargo]}
                                                />
                                            </li>
                                        ))}
                                    </>
                                )}
                            </ul>
                        </li>
                    ))}
                </ul>
            )}
        </li>
    );
};

export default ChangeLogRotationComponent;
